import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { takePostLoginRedirect } from '@/lib/postLoginRedirect';
import { api, User, Organization } from '../lib/api';
import { clearAdvisorStorage } from '../lib/advisor';

interface AuthContextType {
  user: User | null;
  organizations: Organization[];
  currentOrganization: Organization | null;
  loading: boolean;
  // Deployment-wide COVERAGE_TESTING flag, sourced from the current-user
  // response. Defaults false (matches the backend default) so there's no
  // flash of coverage UI before the first auth check resolves.
  coverageTestingEnabled: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGitHub: () => Promise<void>;
  logout: () => void;
  switchOrganization: (orgOrId: string | Organization) => void;
  refreshOrganizations: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const oauthProcessedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const [currentUser, orgs] = await Promise.all([
          api.getCurrentUser(),
          api.getOrganizations(),
        ]);
        setOrganizations(orgs);

        // Resolve the current organization BEFORE exposing the user:
        // data queries are gated on `user`, so the org header must be set
        // on the api client by the time they fire.
        const storedOrgId = localStorage.getItem('organization_id');
        const org = (storedOrgId && orgs.find(o => o.id === storedOrgId)) || orgs[0];
        if (org) {
          setCurrentOrganization(org);
          api.setOrganizationId(org.id);
        }

        setUser(currentUser);
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('organization_id');
      }
    }
    setLoading(false);
  }, []);

  // Handle OAuth callback - only process once
  useEffect(() => {
    if (oauthProcessedRef.current) return;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const oauthError = params.get('error');
    const state = params.get('state');

    // Skip OAuth processing if we are on the reset password page or other pages that use 'token' param
    if (token && location.pathname !== '/reset-password') {
      oauthProcessedRef.current = true;

      // Validate state if present
      const storedState = localStorage.getItem('oauth_state');
      if (state && storedState && state !== storedState) {
        setError('Authentication failed: invalid state');
        localStorage.removeItem('oauth_state');
        setLoading(false);
        navigate('/', { replace: true });
        return;
      }

      localStorage.removeItem('oauth_state');
      api.setToken(token);
      checkAuth().then(() => {
        navigate(takePostLoginRedirect() ?? '/dashboard', { replace: true });
      });
    } else if (oauthError) {
      oauthProcessedRef.current = true;
      localStorage.removeItem('oauth_state');
      setError(`Authentication failed: ${oauthError}`);
      setLoading(false);
      navigate('/', { replace: true });
    } else {
      // No OAuth callback, just check existing auth
      checkAuth();
    }
  }, []); // Only run once on mount

  const refreshOrganizations = async () => {
    try {
      const orgs = await api.getOrganizations();
      setOrganizations(orgs);

      // If current organization is not in the list (e.g. removed), reset it
      if (currentOrganization && !orgs.find(o => o.id === currentOrganization.id)) {
        // Membership was removed: its Advisor conversations are no longer ours to show.
        clearAdvisorStorage();
        if (orgs.length > 0) {
          setCurrentOrganization(orgs[0]);
          api.setOrganizationId(orgs[0].id);
        } else {
          setCurrentOrganization(null);
          api.setOrganizationId('');
        }
      } else if (!currentOrganization && orgs.length > 0) {
        // If no current organization but we have some, set the first one
        setCurrentOrganization(orgs[0]);
        api.setOrganizationId(orgs[0].id);
      }
    } catch (err) {
      console.error('Failed to refresh organizations:', err);
    }
  };

  const switchOrganization = (orgOrId: string | Organization) => {
    let org: Organization | undefined;

    if (typeof orgOrId === 'string') {
      org = (organizations || []).find(o => o.id === orgOrId);
    } else {
      org = orgOrId;
    }

    if (org) {
      // Advisor conversations belong to the organization being left.
      if (org.id !== currentOrganization?.id) clearAdvisorStorage();
      setCurrentOrganization(org);
      api.setOrganizationId(org.id);
      // Reload page to refresh data for new org context
      window.location.reload();
    }
  };

  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      setError(null);
      setLoading(true);
      const response = await api.login({ email, password, rememberMe });

      // Fetch organizations and set the org context before exposing the
      // user, so queries gated on `user` carry the org header.
      const orgs = await api.getOrganizations();
      setOrganizations(orgs);
      if (orgs.length > 0) {
        setCurrentOrganization(orgs[0]);
        api.setOrganizationId(orgs[0].id);
      }
      setUser(response.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      const { url, state } = await api.getGoogleAuthUrl();
      localStorage.setItem('oauth_state', state);
      window.location.href = url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google login failed';
      setError(message);
      throw err;
    }
  };

  const loginWithGitHub = async () => {
    try {
      setError(null);
      const { url, state } = await api.getGitHubAuthUrl();
      localStorage.setItem('oauth_state', state);
      window.location.href = url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'GitHub login failed';
      setError(message);
      throw err;
    }
  };

  const logout = () => {
    clearAdvisorStorage();
    api.logout();
    setUser(null);
    setOrganizations([]);
    setCurrentOrganization(null);
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organizations,
        currentOrganization,
        loading,
        coverageTestingEnabled: user?.coverage_testing_enabled ?? false,
        login,
        loginWithGoogle,
        loginWithGitHub,
        logout,
        switchOrganization,
        refreshOrganizations,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
